import type { SkillDetailDto, SkillDto } from '@dbrain/shared';
import { Alert, Badge, Button, Card, ConfirmDialog, Dialog, EmptyState, Field, Icons, Menu, MenuItem, PageLoading, Select, Skeleton, Switch, TextInput, type IconComponent } from '@dbrain/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useOrganizationAdmin } from '../api/plan.js';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { api } from '../api/client.js';
import { capKeys, useReviewQueue, useSkill, useSkillUsageOf, useSkills } from '../api/capabilities.js';
import { useDashboard, useMe } from '../api/hooks.js';
import { ErrorAlert } from '../components/ErrorAlert.js';
import { InfoTip, LibrarySection, ModeTag, PlaceSelect, ScopeChips, ScopeTag } from '../components/library/Library.js';
import { PageHeader } from '../components/PageHeader.js';
import { OwnLimitCount, OwnLimitNote, useOwnLimit } from '../components/plan/PlanParts.js';
import { SkillAddDialog, type SkillAddTab } from '../components/skills/SkillAddDialog.js';
import { SkillSourceError } from '../components/skills/SkillSourceError.js';
import { FileList, PermissionList } from '../components/skills/SkillParts.js';
import { useCapabilities } from '../experience.js';
import { formatCount } from '@dbrain/app-core/format';
import { adminSections } from './admin/AdminLayout.js';

/**
 * Skills (06 §14; SA-047…SA-055, QW-1201, QW-1206, QW-1207, D-13, D-28; SkillKutuphanesi, D-118):
 * standard, Herkes (the organization's), team, Kişisel (one's own) and workspace skills; with "Tümü" one
 * section per place in the library pages' order, each with what it may run and whether it is on by default;
 * on and off for the user or a workspace; versions with rollback; the organization's review queue. Another
 * team's skills (seen by owners and admins to manage them) are in one closed section at the end. Usage is
 * in Yönetim → Skill analitiği; a skill's managers see its own line on its detail.
 */
type Filter = 'all' | SkillDto['level'];
const CHIPS: Filter[] = ['all', 'organization', 'team', 'user', 'workspace', 'builtin'];

/** A card's icon tile by where the skill lives (SkillKutuphanesi.dc.html). */
const TILES: Record<SkillDto['level'], { icon: IconComponent; tone: string }> = {
  team: { icon: Icons.members, tone: 'pink' },
  organization: { icon: Icons.organization, tone: 'blue' },
  user: { icon: Icons.user, tone: 'purple' },
  workspace: { icon: Icons.workspace, tone: 'green' },
  builtin: { icon: Icons.skills, tone: 'grey' },
};

/** A standard skill's description in the interface's language (the model reads the English one). */
export function useSkillDescription(): (skill: Pick<SkillDto, 'level' | 'name' | 'description'>) => string {
  const { t } = useTranslation();
  return (skill) => (skill.level === 'builtin' ? t(`skills.standard.${skill.name}`, { defaultValue: skill.description }) : skill.description);
}

function SkillTags({ skill, workspaces }: { skill: SkillDto; workspaces: Map<string, string> }) {
  const { t } = useTranslation();
  return (
    <span className="skill-badges">
      {skill.level === 'builtin' ? (
        <>
          <ScopeTag scope="builtin" />
          <span className="scope-tag">{t('library.tags.builtinFrom')}</span>
        </>
      ) : (
        <ScopeTag scope={skill.level} name={skill.level === 'team' ? skill.teamName : skill.level === 'workspace' && skill.workspaceId ? (workspaces.get(skill.workspaceId) ?? null) : null} />
      )}
      <ModeTag mode={skill.catalogMode} />
      {skill.permissions.commands.length > 0 || skill.permissions.ownScripts ? <span className="scope-tag run-tag">{t('skills.mayRun')}</span> : null}
      {skill.latestStatus === 'in_review' ? <Badge tone="warning">{t('skills.status.in_review')}</Badge> : null}
      {skill.latestStatus === 'draft' ? <Badge>{t('skills.status.draft')}</Badge> : null}
      {skill.latestStatus === 'rejected' && !skill.version ? <Badge tone="danger">{t('skills.status.rejected')}</Badge> : null}
      {!skill.userInvocable ? <span className="scope-tag">{t('skills.modelOnly')}</span> : null}
    </span>
  );
}

function SkillCard({ skill, workspaceId, workspaces, selected, onOpen }: { skill: SkillDto; workspaceId: string; workspaces: Map<string, string>; selected: boolean; onOpen: () => void }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const describe = useSkillDescription();
  const toggle = useMutation({
    mutationFn: (enabled: boolean) => api('PUT', `/api/skills/${encodeURIComponent(skill.key)}/enabled`, { enabled, ...(workspaceId ? { workspaceId } : {}) }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: capKeys.skillsAll }),
  });
  const usable = skill.version !== null || skill.level === 'builtin';
  const { icon: Icon, tone } = TILES[skill.level];
  return (
    <li className={selected ? 'skill-card skill-card--selected' : 'skill-card'} data-testid="skill-row" data-name={skill.name} data-level={skill.level}>
      <div className="skill-card__head">
        <span className={`skill-card__tile skill-card__tile--${tone}`} aria-hidden="true">
          <Icon size={18} />
        </span>
        <h3 className="skill-card__name">
          <button type="button" className="skill-card__open" onClick={onOpen} aria-expanded={selected} data-testid="skill-open">
            {skill.name}
          </button>
        </h3>
        {usable ? (
          <Switch
            checked={skill.enabled}
            disabled={skill.enabledScope === 'required' || toggle.isPending}
            onChange={(e) => toggle.mutate(e.target.checked)}
            title={skill.enabledScope === 'required' ? t('skills.catalog.requiredHint') : undefined}
            aria-label={t('skills.toggle', { name: skill.name })}
            data-testid="skill-toggle"
          />
        ) : null}
      </div>
      <p className="skill-card__description">{describe(skill)}</p>
      {skill.shadowedBy ? <p className="skill-card__note">{t('skills.shadowed', { level: t(`skills.levels.${skill.shadowedBy.level}`) })}</p> : null}
      {skill.hides.length > 0 ? <p className="skill-card__note">{t('skills.hides', { levels: skill.hides.map((h) => t(`skills.levels.${h.level}`)).join(', ') })}</p> : null}
      <SkillTags skill={skill} workspaces={workspaces} />
      <ErrorAlert error={toggle.error} />
    </li>
  );
}

function SkillDetail({
  skillKey,
  workspaceId,
  workspaces,
  onClose,
  onEdit,
  onUploadFile,
}: {
  skillKey: string;
  workspaceId: string;
  workspaces: Map<string, string>;
  onClose: () => void;
  onEdit: (skill: SkillDetailDto) => void;
  /** Phase 16c.3: "Dosyadan yükle" of an update's error card opens Skill ekle on Dosyadan */
  onUploadFile: () => void;
}) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const describe = useSkillDescription();
  const detail = useSkill(skillKey, workspaceId ? { workspaceId } : {});
  // its managers see its own usage as one line; everyone's is in Yönetim → Skill analitiği
  const own = useSkillUsageOf(detail.data?.canManage && detail.data.level !== 'builtin' ? detail.data.id : null, 30);
  const [reviewNote, setReviewNote] = useState('');
  const [updatePreview, setUpdatePreview] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const refresh = () => void queryClient.invalidateQueries({ queryKey: capKeys.skillsAll });
  const action = useMutation({
    mutationFn: (request: { method: 'POST' | 'PATCH' | 'DELETE'; path: string; body?: unknown }) => api(request.method, request.path, request.body),
    onSuccess: (_data, request) => {
      refresh();
      if (request.method === 'DELETE') {
        setDeleting(false);
        onClose();
      }
    },
  });
  const skill = detail.data;
  if (detail.isPending) return <PageLoading label={t('common.loading')} />;
  if (!skill) return <ErrorAlert error={detail.error} />;
  const library = skill.level === 'organization' || skill.level === 'team';
  return (
    <Card
      title={skill.name}
      className="skill-detail"
      data-testid="skill-detail"
      actions={<IconButtonClose onClose={onClose} />}
    >
      <SkillTags skill={skill} workspaces={workspaces} />
      <p>{describe(skill)}</p>
      {skill.whenToUse ? <p className="muted">{t('skills.whenToUse', { text: skill.whenToUse })}</p> : null}
      {skill.argumentHint ? <p className="muted">{t('skills.slashHint', { name: skill.name, hint: skill.argumentHint })}</p> : null}
      {skill.source?.url ? (
        <p className="muted">
          {t('skills.source', { url: skill.source.url })}
          {skill.source.commit ? ` · ${skill.source.commit.slice(0, 12)}` : ''}
        </p>
      ) : null}
      <h3>{t('skills.permissions.title')}</h3>
      <PermissionList permissions={skill.permissions} />
      {skill.hooks.length > 0 ? <Alert tone="info" title={t('skills.hooksIgnored', { hooks: skill.hooks.join(', ') })} /> : null}
      {skill.warnings.map((warning) => (
        <Alert key={warning.code} tone="warning" title={t(`skills.warnings.${warning.code}`, { defaultValue: warning.message })} />
      ))}
      <p className="muted">{t('skills.cost', { listing: skill.listingTokens, body: skill.bodyTokens })}</p>
      {own.data ? (
        <p className="muted" data-testid="skill-own-usage">
          {t('skills.usageOwn', { loads: formatCount(own.data.loads, i18n.language), users: formatCount(own.data.users, i18n.language) })}
        </p>
      ) : null}
      <details>
        <summary>{t('skills.add.files', { count: skill.files.length })}</summary>
        <FileList files={skill.files} />
      </details>
      <details>
        <summary>{t('skills.raw')}</summary>
        <pre className="skill-raw">{skill.skillMd}</pre>
      </details>
      {skill.versions.length > 0 ? (
        <>
          <h3>{t('skills.versions')}</h3>
          <table className="skill-versions" data-testid="skill-versions">
            <tbody>
              {skill.versions.map((version) => (
                <tr key={version.id} data-version={version.version}>
                  <th scope="row">v{version.version}</th>
                  <td>
                    {version.published ? <Badge tone="success">{t('skills.inUse')}</Badge> : <Badge>{t(`skills.status.${version.status}`)}</Badge>}
                  </td>
                  <td className="muted">
                    {new Date(version.createdAt).toLocaleDateString(i18n.language)}
                    {version.createdByName ? ` · ${version.createdByName}` : ''}
                    {version.changeNote ? ` · ${version.changeNote}` : ''}
                    {version.reviewNote ? ` · ${t('skills.reviewNote', { note: version.reviewNote })}` : ''}
                  </td>
                  <td className="row">
                    {skill.canManage && !version.published && (version.status === 'published' || version.status === 'draft') ? (
                      <Button size="sm" variant="secondary" busy={action.isPending} onClick={() => action.mutate({ method: 'POST', path: `/api/skills/${skill.id}/rollback`, body: { version: version.version } })} data-testid="skill-rollback">
                        {t('skills.rollback')}
                      </Button>
                    ) : null}
                    {(version.status === 'draft' || version.status === 'rejected') && skill.level === 'organization' && !skill.canManage ? (
                      <Button size="sm" variant="secondary" onClick={() => action.mutate({ method: 'POST', path: `/api/skills/${skill.id}/versions/${version.version}/submit` })}>
                        {t('skills.submit')}
                      </Button>
                    ) : null}
                    {version.status === 'in_review' && skill.canManage ? (
                      <>
                        <Button size="sm" onClick={() => action.mutate({ method: 'POST', path: `/api/skills/${skill.id}/versions/${version.version}/review`, body: { decision: 'approve', ...(reviewNote ? { note: reviewNote } : {}) } })} data-testid="skill-approve">
                          {t('skills.approve')}
                        </Button>
                        <Button size="sm" variant="secondary" onClick={() => action.mutate({ method: 'POST', path: `/api/skills/${skill.id}/versions/${version.version}/review`, body: { decision: 'reject', ...(reviewNote ? { note: reviewNote } : {}) } })}>
                          {t('skills.reject')}
                        </Button>
                      </>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {skill.canManage && skill.versions.some((v) => v.status === 'in_review') ? (
            <TextInput value={reviewNote} onChange={(e) => setReviewNote(e.target.value)} placeholder={t('skills.reviewNotePlaceholder')} aria-label={t('skills.reviewNotePlaceholder')} />
          ) : null}
        </>
      ) : null}
      {library && skill.canManage ? (
        <Field label={t('skills.catalog.label')} hint={t('skills.catalog.hint')}>
          {({ id }) => (
            <Select id={id} value={skill.catalogMode ?? 'available'} onChange={(e) => action.mutate({ method: 'PATCH', path: `/api/skills/${skill.id}`, body: { catalogMode: e.target.value } })} data-testid="skill-catalog-mode">
              <option value="available">{t('skills.catalog.available')}</option>
              <option value="default_on">{t('skills.catalog.default_on')}</option>
              <option value="required">{t('skills.catalog.required')}</option>
            </Select>
          )}
        </Field>
      ) : null}
      <div className="row">
        {skill.level !== 'builtin' && (skill.canManage || skill.level === 'organization') ? (
          <Button size="sm" variant="secondary" onClick={() => onEdit(skill)} data-testid="skill-edit">
            {skill.canManage ? t('skills.edit') : t('skills.propose')}
          </Button>
        ) : null}
        {skill.source?.url && skill.canManage ? (
          <Button size="sm" variant="secondary" onClick={() => setUpdatePreview(true)} data-testid="skill-update-from-source">
            {t('skills.updateFromSource')}
          </Button>
        ) : null}
        <a className="ui-button ui-button--ghost ui-button--sm" href={`/api/skills/${encodeURIComponent(skill.key)}/download${workspaceId ? `?workspaceId=${workspaceId}` : ''}`} download>
          {t('skills.download')}
        </a>
        {skill.canManage && skill.level !== 'builtin' ? (
          <Button size="sm" variant="danger" onClick={() => setDeleting(true)} data-testid="skill-delete">
            {t('common.delete')}
          </Button>
        ) : null}
      </div>
      <ErrorAlert error={deleting ? null : action.error} />
      {updatePreview ? (
        <SourceUpdate
          skill={skill}
          onClose={() => setUpdatePreview(false)}
          onUpload={() => {
            setUpdatePreview(false);
            onUploadFile();
          }}
        />
      ) : null}
      {deleting ? (
        <ConfirmDialog
          title={t('skills.deleteTitle', { name: skill.name })}
          confirmLabel={t('skills.deleteConfirm')}
          cancelLabel={t('common.cancel')}
          closeLabel={t('common.close')}
          busy={action.isPending}
          error={<ErrorAlert error={action.error} />}
          onConfirm={() => action.mutate({ method: 'DELETE', path: `/api/skills/${skill.id}` })}
          onClose={() => {
            setDeleting(false);
            action.reset();
          }}
          data-testid="skill-delete-dialog"
        >
          <p>{t('skills.confirmDelete', { name: skill.name })}</p>
          {skill.level !== 'user' ? <p>{t('skills.deleteShared', { level: t(`skills.levels.${skill.level}`) })}</p> : null}
        </ConfirmDialog>
      ) : null}
    </Card>
  );
}

/**
 * D-13: an update from the recorded source is previewed like an install, then becomes a new version. Phase 16c.3: a
 * GitHub skill's update reads its recorded folder only, and a failure is the same card as in Skill ekle.
 */
function SourceUpdate({ skill, onClose, onUpload }: { skill: SkillDetailDto; onClose: () => void; onUpload: () => void }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const preview = useMutation({ mutationFn: () => api<{ previewId: string; skillMd: string; permissions: SkillDto['permissions'] }>('POST', `/api/skills/${skill.id}/update-from-source`) });
  const install = useMutation({
    mutationFn: (previewId: string) => api('POST', '/api/skills/install', { previewId, skillId: skill.id }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: capKeys.skillsAll });
      onClose();
    },
  });
  return (
    <Dialog title={t('skills.updateFromSource')} closeLabel={t('common.close')} onClose={onClose} size="wide" data-testid="skill-update-dialog">
      {!preview.data ? (
        <Button busy={preview.isPending} onClick={() => preview.mutate()} data-testid="skill-update-preview">
          {t('skills.add.preview')}
        </Button>
      ) : (
        <>
          <PermissionList permissions={preview.data.permissions} />
          <pre className="skill-raw" data-testid="skill-update-skillmd">
            {preview.data.skillMd}
          </pre>
        </>
      )}
      <SkillSourceError error={preview.error} onUpload={onUpload} onRetry={() => preview.mutate()} retrying={preview.isPending} />
      <ErrorAlert error={install.error} />
      <div className="row dialog__actions">
        <Button variant="ghost" onClick={onClose}>
          {t('common.cancel')}
        </Button>
        <Button disabled={!preview.data} busy={install.isPending} onClick={() => preview.data && install.mutate(preview.data.previewId)} data-testid="skill-update-install">
          {t('skills.add.update')}
        </Button>
      </div>
    </Dialog>
  );
}

function ReviewQueue() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const queue = useReviewQueue(true);
  const decide = useMutation({
    mutationFn: (input: { skillId: string; version: number; decision: 'approve' | 'reject' }) =>
      api('POST', `/api/skills/${input.skillId}/versions/${input.version}/review`, { decision: input.decision }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: capKeys.review });
      void queryClient.invalidateQueries({ queryKey: capKeys.skillsAll });
    },
  });
  if (!queue.data || queue.data.length === 0) return null;
  return (
    <Card title={t('skills.review.title', { count: queue.data.length })} data-testid="skill-review-queue">
      <ul className="list">
        {queue.data.map((item) => (
          <li key={item.versionId} className="list__item">
            <div>
              <strong>{item.name}</strong> <span className="muted">v{item.version}</span>
              <p className="muted">{item.description}</p>
              <PermissionList permissions={item.permissions} compact />
              <details>
                <summary>{t('skills.raw')}</summary>
                <pre className="skill-raw">{item.skillMd}</pre>
              </details>
            </div>
            <div className="row">
              <Button size="sm" onClick={() => decide.mutate({ skillId: item.skillId, version: item.version, decision: 'approve' })}>
                {t('skills.approve')}
              </Button>
              <Button size="sm" variant="secondary" onClick={() => decide.mutate({ skillId: item.skillId, version: item.version, decision: 'reject' })}>
                {t('skills.reject')}
              </Button>
            </div>
          </li>
        ))}
      </ul>
      <ErrorAlert error={decide.error} />
    </Card>
  );
}

function IconButtonClose({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  return (
    <button type="button" className="ui-icon-button ui-icon-button--sm" aria-label={t('common.close')} title={t('common.close')} onClick={onClose} data-testid="skill-detail-close">
      <Icons.close size={16} />
    </button>
  );
}

interface Group {
  key: string;
  title: string;
  hint: string;
  skills: SkillDto[];
  /** another team's skills, seen for management: one closed section at the end */
  other?: boolean;
}

/** The sections in the library pages' order: each team of mine, Herkes, Kişisel, Çalışma alanları, Standart; others' teams last. */
function groupsOf(skills: SkillDto[], t: (key: string, options?: Record<string, unknown>) => string): Group[] {
  const teams = new Map<string, SkillDto[]>();
  for (const skill of skills) {
    if (skill.level !== 'team' || skill.teamMember === false) continue;
    const list = teams.get(skill.teamId ?? '') ?? [];
    list.push(skill);
    teams.set(skill.teamId ?? '', list);
  }
  const mine = [...teams.entries()]
    .map(([teamId, list]) => ({ teamId, list, name: list[0]?.teamName ?? t('library.scopes.team') }))
    .sort((a, b) => a.name.localeCompare(b.name));
  const of = (level: SkillDto['level']) => skills.filter((s) => s.level === level);
  const others = skills.filter((s) => s.level === 'team' && s.teamMember === false);
  return [
    ...mine.map(({ teamId, list, name }) => ({
      key: `team-${teamId}`,
      title: t('skills.sections.teamTitle', { team: name }),
      // a domain's team skills are on for its members (D-117)
      hint: t(list.some((s) => s.catalogMode === 'default_on' || s.catalogMode === 'required') ? 'skills.sections.teamAuto' : 'skills.sections.team', { team: name }),
      skills: list,
    })),
    { key: 'organization', title: t('library.scopes.organization'), hint: t('skills.sections.organization'), skills: of('organization') },
    { key: 'user', title: t('library.scopes.user'), hint: t('skills.sections.user'), skills: of('user') },
    { key: 'workspace', title: t('library.scopes.workspace'), hint: t('skills.sections.workspace'), skills: of('workspace') },
    { key: 'builtin', title: t('library.scopes.builtin'), hint: t('skills.sections.builtin'), skills: of('builtin') },
    { key: 'other-teams', title: t('skills.sections.otherTeams'), hint: t('skills.sections.otherTeamsHint'), skills: others, other: true },
  ];
}

const inFilter = (group: Group, filter: Filter) =>
  filter === 'all' || (filter === 'team' ? group.key.startsWith('team-') || group.key === 'other-teams' : group.key === filter);

export function SkillsPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const me = useMe();
  const capabilities = useCapabilities();
  const dashboard = useDashboard();
  const admin = useOrganizationAdmin();
  const analytics = me.data ? adminSections(me.data, capabilities).includes('skills') : false;
  const [params, setParams] = useSearchParams();
  const [workspaceId, setWorkspaceId] = useState('');
  const [filter, setFilter] = useState<Filter>('all');
  const [query, setQuery] = useState('');
  // the skill shown on the right; a saved or installed one opens here (?open=)
  const open = params.get('open');
  const setOpen = (key: string | null) =>
    setParams(
      (current) => {
        const copy = new URLSearchParams(current);
        if (key) copy.set('open', key);
        else copy.delete('open');
        return copy;
      },
      { replace: true },
    );
  const [adding, setAdding] = useState<SkillAddTab | null>(null);
  // 16c.5 (D-406): Bağlantı ekle's "Skill ekle'ye git" opens Skill ekle on Bağlantıdan with the link (?add=link&source=)
  const [addSource, setAddSource] = useState('');
  useEffect(() => {
    if (params.get('add') !== 'link') return;
    setAddSource(params.get('source') ?? '');
    setAdding('link');
    setParams(
      (current) => {
        const copy = new URLSearchParams(current);
        copy.delete('add');
        copy.delete('source');
        return copy;
      },
      { replace: true },
    );
  }, [params, setParams]);
  const skills = useSkills(workspaceId ? { workspaceId } : {});
  const describe = useSkillDescription();
  const workspaceList = (dashboard.data?.workspaces ?? []).map((w) => ({ id: w.id, name: w.name }));
  const workspaces = new Map(workspaceList.map((w) => [w.id, w.name]));
  const all = skills.data ?? [];
  const words = query.trim().toLocaleLowerCase(i18n.language);
  const matches = all.filter((s) => !words || `${s.name} ${describe(s)}`.toLocaleLowerCase(i18n.language).includes(words));
  const groups = groupsOf(matches, t).filter((g) => inFilter(g, filter));
  const shown = filter === 'all' ? groups.filter((g) => g.skills.length > 0) : groups.filter((g) => g.skills.length > 0 || !g.other);
  const count = (level: SkillDto['level']) => all.filter((s) => s.level === level).length;
  const active = all.filter((s) => s.enabled && !s.shadowedBy && s.modelInvocable);
  const listing = active.reduce((sum, s) => sum + s.listingTokens, 0);
  const card = (skill: SkillDto) => (
    <SkillCard key={skill.key} skill={skill} workspaceId={workspaceId} workspaces={workspaces} selected={open === skill.key} onOpen={() => setOpen(open === skill.key ? null : skill.key)} />
  );
  // 16c.1 (B2cSkillSiniri): the package's own skills ("3 / 3"); at the limit "Skill ekle" waits and the note says why
  const own = useOwnLimit('skills');
  const full = own?.full === true;
  return (
    <div className="page page--wide skills-page">
      <PageHeader
        title={t('skills.title')}
        intro={t('skills.introShort')}
        actions={
          <span className="own-limit__actions">
            {own && own.limit !== null ? <OwnLimitCount kind="skills" used={own.used} limit={own.limit} /> : null}
            {full ? (
              <Button size="lg" variant="secondary" icon={<Icons.lock size={16} />} disabled title={t('b2cModels.skills.locked')} data-testid="skill-add-locked">
                {t('skills.add.button')}
              </Button>
            ) : (
          <Menu
            align="right"
            className="skills-add"
            triggerClassName="ui-button ui-button--primary ui-button--lg"
            trigger={
              <>
                <Icons.plus size={18} />
                {t('skills.add.button')}
              </>
            }
            panelClassName="skills-add__panel"
            data-testid="skill-add"
          >
            <MenuItem icon={<Icons.connections />} onClick={() => setAdding('link')} data-testid="skill-add-url">
              {t('skills.addMenu.url')}
            </MenuItem>
            <MenuItem icon={<Icons.upload />} onClick={() => setAdding('file')} data-testid="skill-add-upload">
              {t('skills.addMenu.upload')}
            </MenuItem>
            <MenuItem icon={<Icons.edit />} onClick={() => setAdding('write')} data-testid="skill-new">
              {t('skills.addMenu.form')}
            </MenuItem>
            <MenuItem icon={<Icons.chat />} onClick={() => setAdding('conversation')} data-testid="skill-add-conversation">
              {t('skills.addMenu.conversation')}
            </MenuItem>
          </Menu>
            )}
          </span>
        }
      />
      {full && own?.limit !== null && own ? <OwnLimitNote kind="skills" limit={own.limit ?? 0} /> : null}
      <div className="library-toolbar">
        <ScopeChips
          label={t('library.filter')}
          testId="skills-level"
          active={filter}
          onChange={setFilter}
          items={CHIPS.map((key) => (key === 'all' ? { key, label: t('library.scopes.all') } : { key, label: t(`library.scopes.${key}`), count: count(key) }))}
        />
        <div className="library-toolbar__end">
          <PlaceSelect value={workspaceId} onChange={setWorkspaceId} workspaces={workspaceList} hint={t('skills.placeHint')} testId="skills-place" />
          <label className="search-pill">
            <Icons.search size={16} />
            <span className="ui-visually-hidden">{t('skills.searchLabel')}</span>
            <input type="search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t('skills.searchLabel')} data-testid="skills-search" />
          </label>
        </div>
      </div>
      {skills.data ? (
        <p className="library-summary" data-testid="skills-summary">
          <span>
            {t('skills.summaryNow')} <strong>{t('skills.summaryOn', { count: active.length })}</strong>
          </span>
          <InfoTip label={t('skills.summaryTipLabel')} text={t('skills.summaryTip', { tokens: formatCount(listing, i18n.language) })} testId="skills-summary-tip" />
          <span aria-hidden="true"> · </span>
          <span>{t('skills.summarySlash')}</span>
        </p>
      ) : null}
      <ErrorAlert error={skills.error} />
      <div className={open ? 'skills-layout skills-layout--open' : 'skills-layout'}>
        <div data-testid="skill-list">
          {skills.isPending ? <Skeleton block /> : null}
          {skills.data && shown.every((g) => g.skills.length === 0) ? <EmptyState icon={<Icons.skills />} title={t('skills.empty')} /> : null}
          {shown
            .filter((g) => g.skills.length > 0)
            .map((group) => (
              <LibrarySection
                key={group.key}
                title={group.title}
                hint={group.hint}
                count={t('skills.sections.count', { count: group.skills.length })}
                testId={`skills-section-${group.key}`}
                {...(group.other ? { collapsed: { note: t('skills.sections.otherTeamsNote') } } : {})}
              >
                <ul className="skill-grid">{group.skills.map(card)}</ul>
              </LibrarySection>
            ))}
          {analytics ? (
            <p className="library-foot">
              <Link to="/admin/skills" data-testid="skills-analytics-link">
                {t('skills.analyticsLink')}
              </Link>
            </p>
          ) : null}
        </div>
        {open ? (
          <SkillDetail
            skillKey={open}
            workspaceId={workspaceId}
            workspaces={workspaces}
            onClose={() => setOpen(null)}
            onEdit={(skill) => void navigate(`/skills/${encodeURIComponent(skill.key)}/edit`)}
            onUploadFile={() => {
              setOpen(null);
              setAdding('file');
            }}
          />
        ) : null}
      </div>
      {admin ? <ReviewQueue /> : null}
      {adding ? (
        <SkillAddDialog
          onClose={() => {
            setAdding(null);
            setAddSource('');
          }}
          workspaceId={workspaceId || undefined}
          initialTab={adding}
          initialSource={addSource}
        />
      ) : null}
    </div>
  );
}
